import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import {
  HeroBannerHighlight,
  HeroBannerContainer,
  HeroBannerTitle,
  HeroBannerTitleContainer,
  HeroBannerWrapper,
  HeroBannerFormBlock,
  HeroBannerInputContainer,
  HeroBannerInputField,
  HeroBannerLocationIcon,
  HeroBannerInput,
  HeroBannerSubmitButton,
  HeroStickyBar,
  HeroStickyInputField,
  HeroStickyInput,
  HeroStickySubmitButton,
  HeroStickyChangeZipButton,
  HeroStickyBarContainer,
} from "../styles/components";
import { usePlanContext } from "../../consteNew/context/planContext";
import ZipCodeErrorModal from "./ZipCodeErrorModal";

interface HeroBannnerProps {
  planSectionRef?: React.RefObject<HTMLDivElement>;
}

const LOCATION_ICON = "/images/constenewImg/tdesign_location-filled.svg";

const HeroBannner = ({ planSectionRef }: HeroBannnerProps) => {
  const {
    zipCode,
    setZipCode,
    plansLoading,
    zipErrorModal,
    setZipErrorModal,
    plans,
    saveZipToStorage,
    plansUnavailableReason,
  } = usePlanContext();

  const [localZipCode, setLocalZipCode] = useState(zipCode);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showStickyBar, setShowStickyBar] = useState(false);
  const shouldScrollRef = useRef(false);
  const heroFormRef = useRef<HTMLDivElement>(null);
  const stickyZipInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalZipCode(zipCode);
  }, [zipCode]);

  useEffect(() => {
    if (zipErrorModal) {
      setZipErrorModal(false);
    }
  }, [zipErrorModal, setZipErrorModal]);

  useEffect(() => {
    if (
      shouldScrollRef.current &&
      !plansLoading &&
      (plans.length > 0 || plansUnavailableReason === "api_error")
    ) {
      handleScroll();
      shouldScrollRef.current = false;
    }
  }, [plansLoading, plans, plansUnavailableReason]);

  useEffect(() => {
    const target = heroFormRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyBar(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "-58px 0px 0px 0px",
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^\d{0,5}$/.test(value)) {
      setLocalZipCode(value);
    }
  };

  const handleScroll = () => {
    const element = planSectionRef?.current;
    if (!element) return;

    const navbarHeight = 80;
    const elementPosition =
      element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - navbarHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const handleViewPlans = async () => {
    if (loading || plansLoading) return;

    setLoading(true);
    saveZipToStorage(localZipCode);

    if (!localZipCode || localZipCode.length !== 5) {
      setModalMessage("Please check your ZIP format");
      setModalOpen(true);
      setLoading(false);
      return;
    }

    shouldScrollRef.current = true;

    if (!plansLoading && (plans.length > 0 || plansUnavailableReason === "api_error")) {
      setTimeout(() => handleScroll(), 100);
    }

    setZipCode(localZipCode);
    setLoading(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void handleViewPlans();
    }
  };

  const handleChangeZipClick = () => {
    const input = stickyZipInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
    setLocalZipCode("");
  };

  const isSubmitting = loading || plansLoading;

  return (
    <HeroBannerWrapper>
      <HeroBannerContainer>
        <HeroBannerTitleContainer>
          <HeroBannerTitle>
            Be Sure with Constellation.
            <br />
            <HeroBannerHighlight>Texas Sure.</HeroBannerHighlight>
            <br />
            <HeroBannerFormBlock ref={heroFormRef}>
              <HeroBannerInputContainer>
                <HeroBannerInputField>
                  <HeroBannerLocationIcon
                    src={LOCATION_ICON}
                    alt=""
                    aria-hidden="true"
                  />
                  <HeroBannerInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    placeholder="Enter your Zip Code"
                    value={localZipCode}
                    onChange={handleZipCodeChange}
                    onKeyDown={handleKeyDown}
                    aria-label="Enter your Zip Code"
                    disabled={isSubmitting}
                  />
                </HeroBannerInputField>
                <HeroBannerSubmitButton
                  type="button"
                  onClick={() => void handleViewPlans()}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} sx={{ color: "#FFF" }} />
                  ) : (
                    "View Plans"
                  )}
                </HeroBannerSubmitButton>
              </HeroBannerInputContainer>
            </HeroBannerFormBlock>
          </HeroBannerTitle>
        </HeroBannerTitleContainer>
      </HeroBannerContainer>

      <HeroStickyBar visible={showStickyBar}>
        <HeroStickyBarContainer>
          <HeroStickyInputField>
            <HeroBannerLocationIcon
              src={LOCATION_ICON}
              alt=""
              aria-hidden="true"
            />
            <HeroStickyInput
              ref={stickyZipInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={5}
              placeholder="Enter your Zip Code"
              value={localZipCode}
              onChange={handleZipCodeChange}
              onKeyDown={handleKeyDown}
              aria-label="Enter your Zip Code"
              disabled={isSubmitting}
            />
          </HeroStickyInputField>
         <Box sx={{ display: "flex", gap: 1 }}>
         <HeroStickyChangeZipButton
            type="button"
            onClick={handleChangeZipClick}
          >
            Change Zip
          </HeroStickyChangeZipButton>
          <HeroStickySubmitButton
            type="button"
            onClick={() => void handleViewPlans()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={18} sx={{ color: "#FFF" }} />
            ) : (
              "View Plans"
            )}
          </HeroStickySubmitButton>
         </Box>
        </HeroStickyBarContainer>
      </HeroStickyBar>

      <ZipCodeErrorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        message={modalMessage}
      />
    </HeroBannerWrapper>
  );
};

export default HeroBannner;
